import { Component, input, model } from '@angular/core';
import { ContactFormErrors, ContactFormValue, formatPhone } from './contact-form.store';

type TextField = 'firstName' | 'lastName' | 'email' | 'phone';

/** Name / email / phone inputs, two-way bound to the shared contact responses. */
@Component({
  selector: 'app-contact-fields',
  standalone: true,
  template: `
    <div class="name-row">
      <div>
        <input class="input" placeholder="First name" autocomplete="given-name"
          [value]="value().firstName" (input)="update('firstName', $event)" />
        @if (errors().firstName) { <p class="field-error">{{ errors().firstName }}</p> }
      </div>
      <div>
        <input class="input" placeholder="Last name" autocomplete="family-name"
          [value]="value().lastName" (input)="update('lastName', $event)" />
        @if (errors().lastName) { <p class="field-error">{{ errors().lastName }}</p> }
      </div>
    </div>

    <div class="field">
      <input class="input" type="email" placeholder="Email" autocomplete="email"
        [value]="value().email" (input)="update('email', $event)" />
      @if (errors().email) { <p class="field-error">{{ errors().email }}</p> }
    </div>

    <div class="field">
      <input class="input" type="tel" autocomplete="tel"
        [placeholder]="phoneRequired() ? 'Phone number' : 'Phone number (optional)'"
        [value]="value().phone" (input)="update('phone', $event)" />
      @if (errors().phone) { <p class="field-error">{{ errors().phone }}</p> }
    </div>
  `,
  styles: `
    :host { display: block; }
    .name-row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 12px; }
    .field { margin-bottom: 12px; }
    .input {
      width: 100%; padding: 12px 14px; font-size: 16px; font-family: inherit; color: var(--kin-text);
      border: 1px solid var(--kin-neutral-300); border-radius: var(--kin-radius-md);
      background: var(--kin-white); box-sizing: border-box;
      transition: border-color 180ms var(--kin-ease-out);
    }
    .input::placeholder { color: var(--kin-text-tertiary); }
    .input:hover { border-color: var(--kin-neutral-500); }
    .input:focus { outline: none; border-color: var(--kin-neutral-1000); box-shadow: var(--kin-shadow-focus); }
    .field-error { color: var(--kin-red-600); font-size: 12px; font-weight: 500; margin: 4px 0 0; }
    @media (max-width: 600px) {
      .name-row { grid-template-columns: 1fr; }
    }
  `,
})
export class ContactFieldsComponent {
  readonly value = model.required<ContactFormValue>();
  readonly errors = model<ContactFormErrors>({});
  readonly phoneRequired = input(true);

  protected update(field: TextField, event: Event): void {
    const el = event.target as HTMLInputElement;
    const next = field === 'phone' ? formatPhone(el.value) : el.value;
    // Keep the DOM in sync when formatting rejects a keystroke (signal value unchanged → no re-render)
    if (el.value !== next) el.value = next;

    this.value.update(v => ({ ...v, [field]: next }));
    if (this.errors()[field]) this.errors.update(e => ({ ...e, [field]: undefined }));
  }
}
